// The words a tenant uses for a record kind, where customers differ (Aaron 2026-10-11):
//   - jobs: "some customers call it Jobs others call it Work Orders mustang and g2 are Work
//     Orders. demo should be jobs", then "Work Orders by default";
//   - TDs: "similar to Jobs and Work Orders (W/Os) we need TDs and Service Orders S/Os",
//     then "S/Os by default, demo = TDs".
//
// ONE config row per kind per tenant, labels only: config_function `global`, config_variable
// `global_<kind>_caption` (the `global_gst_caption` convention), its value one of the kind's
// option names. /v6/config strips the function prefix, so it reaches useGlobalConfig() as
// `<kind>_caption` (src/hooks/__tests__/configKeyShape.test.ts). A missing row, an empty value
// or an unknown one reads as the kind's `fallback`. Routes, endpoints, field names, DB columns
// and config keys keep their names; only the words a user reads follow this.
//
// Read with the rest of global config: once per page load (the GlobalConfig singleton),
// through the axios cache (1 day, localStorage). A changed row reaches a browser that
// already read global config only after that cache expires or is cleared.
import { useMemo } from "react";

import { useGlobalConfig } from "./useGlobalConfig";

export interface Term {
  /** "Work Orders" / "Jobs" */
  plural: string;
  /** Short plural, for tabs and tight labels: "W/Os" / "Jobs" */
  short: string;
  /** "Work Order" / "Job" */
  singular: string;
  /** Short singular, for actions, toasts and ids: "W/O" / "Job" ("Create new W/O", "W/O #12") */
  shortSingular: string;
}

export const WORK_ORDERS_TERM: Term = { plural: "Work Orders", short: "W/Os", singular: "Work Order", shortSingular: "W/O" };
export const JOBS_TERM: Term = { plural: "Jobs", short: "Jobs", singular: "Job", shortSingular: "Job" };
export const SERVICE_ORDERS_TERM: Term = {
  plural: "Service Orders",
  short: "S/Os",
  singular: "Service Order",
  shortSingular: "S/O",
};
export const TDS_TERM: Term = { plural: "TDs", short: "TDs", singular: "TD", shortSingular: "TD" };

interface TermKind {
  /** The global config key as useGlobalConfig() returns it (stored as `global_<key>`). */
  key: string;
  /** Config value -> words. Matched case-insensitively, trimmed. */
  options: Record<string, Term>;
  /** The option a missing / empty / unknown value reads as: the ONE place a default is set. */
  fallback: string;
}

export const TERM_KINDS = {
  workorders: {
    key: "jobs_caption",
    options: { "Work Orders": WORK_ORDERS_TERM, Jobs: JOBS_TERM },
    fallback: "Work Orders",
  },
  tds: {
    key: "tds_caption",
    options: { "Service Orders": SERVICE_ORDERS_TERM, TDs: TDS_TERM },
    fallback: "Service Orders",
  },
} as const satisfies Record<string, TermKind>;

export type TermKindName = keyof typeof TERM_KINDS;

/** The words for a config value of `kind`; the kind's fallback for anything it does not name. */
export function termFor(kind: TermKindName, caption: unknown): Term {
  const { options, fallback } = TERM_KINDS[kind] as TermKind;
  const wanted = String(caption ?? "").trim().toLowerCase();
  const match = Object.keys(options).find((name) => name.toLowerCase() === wanted);
  return options[match ?? fallback];
}

/** The tenant's words for `kind`, from global config; the fallback until config has loaded. */
export function useTerm(kind: TermKindName): Term {
  const caption = useGlobalConfig()?.[TERM_KINDS[kind].key];
  return useMemo(() => termFor(kind, caption), [kind, caption]);
}

/** A job: W/Os (default) or Jobs -- `global_jobs_caption`. */
export const useWorkOrderTerm = (): Term => useTerm("workorders");

/** A TD (service_orders): S/Os (default) or TDs -- `global_tds_caption`. */
export const useServiceOrderTerm = (): Term => useTerm("tds");
